import { useEffect, useMemo, useState } from 'react';
import { trpc } from '../lib/api';
import {
  Card,
  Button,
  Space,
  Typography,
  DatePicker,
  Select,
  Row,
  Col,
  Form,
  message,
  Input,
  theme,
} from 'antd';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import {
  PlusOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  ShoppingCartOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PermissionButton } from '../components/common/PermissionButton';
import dayjs from 'dayjs';
import { NumericInput, PageContainer, PageHeader, withDevModalFilePath } from '../components/common';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { useToken } = theme;
const { Modal } = withDevModalFilePath('client/src/pages/PartConsumptionLogsPage.tsx');

interface ConsumptionLog {
  log: {
    id: string;
    partId: string;
    workHistoryId?: string | null;
    equipmentId?: string | null;
    quantity: number;
    consumedAt: string;
    consumedBy: string;
    notes?: string | null;
  };
  part: {
    id: string;
    name: string;
    displayId: string;
    unit?: string | null;
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeConsumptionLog = (value: unknown): ConsumptionLog | null => {
  if (!isRecord(value) || !isRecord(value.log) || !isRecord(value.part)) {
    return null;
  }
  if (typeof value.log.id !== 'string' || typeof value.log.partId !== 'string' || typeof value.part.id !== 'string') {
    return null;
  }

  return {
    log: {
      id: value.log.id,
      partId: value.log.partId,
      workHistoryId: typeof value.log.workHistoryId === 'string' ? value.log.workHistoryId : null,
      equipmentId: typeof value.log.equipmentId === 'string' ? value.log.equipmentId : null,
      quantity: typeof value.log.quantity === 'number' ? value.log.quantity : 0,
      consumedAt: typeof value.log.consumedAt === 'string' ? value.log.consumedAt : '',
      consumedBy: typeof value.log.consumedBy === 'string' ? value.log.consumedBy : '',
      notes: typeof value.log.notes === 'string' ? value.log.notes : null,
    },
    part: {
      id: value.part.id,
      name: typeof value.part.name === 'string' ? value.part.name : '',
      displayId: typeof value.part.displayId === 'string' ? value.part.displayId : '',
      unit: typeof value.part.unit === 'string' ? value.part.unit : null,
    },
  };
};

export function PartConsumptionLogsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useToken();
  const utils = trpc.useUtils();
  const queryPartId = searchParams.get('partId') ?? undefined;
  const queryKey = searchParams.toString();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPartId, setSelectedPartId] = useState<string | undefined>(queryPartId);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(searchParams.get('action') === 'create');
  const [openedQueryKey, setOpenedQueryKey] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const pageSize = 20;

  // 消費ログ一覧取得
  const { data: logsData, isLoading, refetch } = trpc.partConsumptionLogs.list.useQuery({
    page: currentPage,
    limit: pageSize,
    partId: selectedPartId,
    startDate: dateRange?.[0].toDate(),
    endDate: dateRange?.[1].toDate(),
  });

  // 部品・消耗品一覧取得
  const { data: partsData, isLoading: isLoadingParts } = trpc.parts.list.useQuery({
    page: 1,
    limit: 1000,
  });

  const hasParts = (partsData?.pagination?.total ?? 0) > 0;

  const openCreateModal = (partId?: string) => {
    form.setFieldsValue({
      partId: partId ?? selectedPartId,
      consumedAt: form.getFieldValue('consumedAt') ?? dayjs(),
    });
    setIsCreateModalVisible(true);
  };

  useEffect(() => {
    if (!queryPartId) {
      return;
    }

    setSelectedPartId(queryPartId);
    setCurrentPage(1);
    form.setFieldsValue({
      partId: queryPartId,
      consumedAt: form.getFieldValue('consumedAt') ?? dayjs(),
    });

    if (searchParams.get('action') === 'create' && openedQueryKey !== queryKey) {
      setIsCreateModalVisible(true);
      setOpenedQueryKey(queryKey);
    }
  }, [form, openedQueryKey, queryKey, queryPartId, searchParams]);

  // 消費ログ作成mutation
  const createMutation = trpc.partConsumptionLogs.create.useMutation({
    onSuccess: async () => {
      messageApi.success('消費ログを記録しました');
      form.resetFields();
      setIsCreateModalVisible(false);
      await Promise.all([
        refetch(),
        utils.parts.list.invalidate(),
      ]);
    },
    onError: (error) => {
      messageApi.error(`消費記録に失敗しました: ${error.message}`);
    },
  });

  // 消費ログ削除mutation
  const deleteMutation = trpc.partConsumptionLogs.delete.useMutation({
    onSuccess: () => {
      messageApi.success('消費ログを削除しました');
      refetch();
    },
    onError: (error) => {
      messageApi.error(`削除に失敗しました: ${error.message}`);
    },
  });

  const logs = (logsData?.items ?? []).flatMap((item) => {
    const normalized = normalizeConsumptionLog(item);
    return normalized ? [normalized] : [];
  });
  const total = logsData?.pagination?.total || 0;
  const parts = useMemo(() => partsData?.items ?? [], [partsData?.items]);
  const partOptions = useMemo(
    () =>
      parts.map(p => ({
        label: `${p.name} (${p.displayId})`,
        value: p.id,
      })),
    [parts]
  );
  const partRecordOptions = useMemo(
    () =>
      parts.map(p => ({
        label: `${p.name} (${p.displayId}) - 在庫: ${p.currentStockLevel}${p.unit ?? '個'}`,
        value: p.id,
      })),
    [parts]
  );

  // 消費記録フォーム送信
  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      createMutation.mutate({
        partId: values.partId,
        quantity: values.quantity,
        consumedAt: values.consumedAt.toDate(),
        notes: values.notes,
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // 削除確認
  const handleDelete = (log: ConsumptionLog) => {
    Modal.confirm({
      title: '消費ログの削除',
      content: (
        <div>
          <p>{log.part.name} - {log.log.quantity}{log.part.unit ?? '個'}</p>
          <p>在庫を復元しますか？</p>
        </div>
      ),
      okText: '削除（在庫復元あり）',
      cancelText: 'キャンセル',
      onOk: () => {
        deleteMutation.mutate({
          id: log.log.id,
          restoreStock: true,
          reason: '誤記録のため削除',
        });
      },
    });
  };

  // テーブルカラム
  const columns: ResponsiveColumnType<ConsumptionLog>[] = [
    {
      title: '消費日時',
      dataIndex: ['log', 'consumedAt'],
      key: 'consumedAt',
      width: 180,
      render: (date) => new Date(date).toLocaleString('ja-JP'),
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '部品・消耗品ID',
      dataIndex: ['part', 'displayId'],
      key: 'partDisplayId',
      width: 120,
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '部品・消耗品名',
      dataIndex: ['part', 'name'],
      key: 'partName',
      render: (text) => <Text strong>{text}</Text>,
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '消費数',
      dataIndex: ['log', 'quantity'],
      key: 'quantity',
      width: 120,
      align: 'right',
      render: (qty, record) => (
        <Text strong style={{ color: '#ff4d4f' }}>
          -{qty} {record.part.unit ?? '個'}
        </Text>
      ),
      mobileRole: 'tag',
      mobilePriority: 3,
    },
    {
      title: '備考',
      dataIndex: ['log', 'notes'],
      key: 'notes',
      render: (notes) => notes || '-',
      mobileRole: 'description',
      mobilePriority: 5,
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <PermissionButton
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record)}
          requiredPermission="manage"
        >
          削除
        </PermissionButton>
      ),
      mobileRole: 'hidden',
    },
  ];

  return (
    <PageContainer>
      {contextHolder}

      {/* ヘッダー */}
      <PageHeader
        title="部品・消耗品消費管理"
        subtitle="部品・消耗品の消費記録を管理します"
        extra={
          <Space wrap>
            <Button
              icon={<AppstoreOutlined aria-hidden="true" />}
              onClick={() => navigate('/app/purchase-orders/category-orders')}
            >
              カテゴリ別 発注・消費記録へ
            </Button>
            <PermissionButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openCreateModal()}
              requiredPermission="edit"
            >
              消費記録
            </PermissionButton>
          </Space>
        }
      />

      {/* 部品が0件の場合 - 部品登録への誘導バナー */}
      {!isLoadingParts && !hasParts && (
        <Card
          style={{
            marginBottom: 16,
            background: token.colorInfoBg,
            border: `1px solid ${token.colorInfoBorder}`,
          }}
        >
          <Row align="middle" gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <AppstoreOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
            </Col>
            <Col flex={1}>
              <Title level={5} style={{ marginBottom: 4 }}>
                まず部品・消耗品を登録しましょう
              </Title>
              <Text type="secondary">
                部品・消耗品を登録すると、以下の機能が利用できるようになります。
              </Text>
            </Col>
            <Col>
              <Button type="primary" onClick={() => navigate('/app/parts')}>
                部品・消耗品を登録する
              </Button>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <BarChartOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
                  <div>
                    <Text strong>在庫管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      在庫数を把握し、不足を事前に検知
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <ShoppingCartOutlined style={{ fontSize: 20, color: token.colorWarning }} />
                  <div>
                    <Text strong>発注管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      発注履歴を記録し、コストを管理
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ height: '100%', background: token.colorBgContainer }}>
                <Space>
                  <HistoryOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                  <div>
                    <Text strong>消費管理</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      使用履歴を追跡し、消耗パターンを分析
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>
      )}

      {/* フィルタ */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" wrap>
          <div>
            <Text strong style={{ marginRight: 8 }}>部品・消耗品:</Text>
            <Select
              placeholder="部品・消耗品で絞込"
              style={{ width: 300 }}
              value={selectedPartId}
              onChange={setSelectedPartId}
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={partOptions}
            />
          </div>
          <div>
            <Text strong style={{ marginRight: 8 }}>期間:</Text>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
            />
          </div>
          <Button
            onClick={() => {
              setSelectedPartId(undefined);
              setDateRange(null);
              setCurrentPage(1);
            }}
          >
            クリア
          </Button>
        </Space>
      </Card>

      {/* 消費ログテーブル */}
      <Card>
        <ResponsiveTable<ConsumptionLog>
          columns={columns}
          dataSource={logs}
          loading={isLoading}
          rowKey={(record) => record.log.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: false,
            showTotal: (total) => `全 ${total} 件`,
            onChange: (page) => setCurrentPage(page),
          }}
          mobileCardConfig={{
            titleField: (record) => record.part?.name ?? '',
            actions: (record) => (
              <PermissionButton
                type="link"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
                requiredPermission="manage"
              >
                削除
              </PermissionButton>
            ),
          }}
        />
      </Card>

      {/* 消費記録モーダル */}
      <Modal
        title="部品・消耗品消費記録"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={handleCreate}
        confirmLoading={createMutation.isPending}
        okText="記録"
        cancelText="キャンセル"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="部品・消耗品"
            name="partId"
            rules={[{ required: true, message: '部品・消耗品を選択してください' }]}
          >
            <Select
              placeholder="部品・消耗品を選択"
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={partRecordOptions}
            />
          </Form.Item>

          <Form.Item
            label="消費数"
            name="quantity"
            rules={[
              { required: true, message: '消費数を入力してください' },
              { type: 'number', min: 1, message: '1以上の値を入力してください' },
            ]}
          >
            <NumericInput
              min={1}
              style={{ width: '100%' }}
              placeholder="消費数を入力"
            />
          </Form.Item>

          <Form.Item
            label="消費日時"
            name="consumedAt"
            rules={[{ required: true, message: '消費日時を選択してください' }]}
            initialValue={dayjs()}
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>

          <Form.Item label="備考" name="notes">
            <TextArea rows={3} placeholder="消費理由や作業内容を入力" />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
}
